import React, { useEffect, useRef, useState } from "react";

import {
  ChevronDown,
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import useBulkAddons from "../../services/Order/useBulkAddons";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";
import useBulkPackages from "../../services/Order/useBulkPackages";

const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

const EMPTY_ADDON = {
  addon_id: "",
  restaurant_id: "",
  addon_name: "",
  linked_item_id: "",
  linked_variant_id: "",
  price_type: "",
  price: "",
  status: "ACTIVE",
};

const EMPTY_PACKAGE_ADDON = {
  package_id: "",
  addon_id: "",
  new_package_id: "",
  new_addon_id: "",
};

export function BulkAddons() {
  const {
    addonsLoading,
    addonSaving,
    addonDeletingId,
    getAddons,
    insertAddon,
    updateAddon,
    deleteAddon,
  } = useBulkAddons();
  const { getRestaurants, getItems, getItemVariants } = useBulkCatalogue();

 
const [rows, setRows] = useState([]);
const [restaurants, setRestaurants] = useState([]);
const [items, setItems] = useState([]);
const [variants, setVariants] = useState([]);
const [form, setForm] = useState(EMPTY_ADDON);
const [modal, setModal] = useState(false);

const [addonSearch, setAddonSearch] = useState("");

const filteredAddons = rows.filter((addon) => {
  const normalize = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const query = normalize(addonSearch.trim());

  if (!query) return true;

  const restaurant = restaurants.find(
    (r) => String(r?.restaurant_id ?? r?.id) === String(addon?.restaurant_id),
  );

  const linkedItem = items.find(
    (item) =>
      String(item?.item_id ?? item?.id) === String(addon?.linked_item_id),
  );

  const linkedVariant = variants.find(
    (variant) =>
      String(variant?.variant_id ?? variant?.id) ===
      String(addon?.linked_variant_id),
  );

  const searchableFields = [
    addon?.addon_id,
    addon?.addon_name,
    addon?.restaurant_id,
    restaurant?.restaurant_name,
    addon?.linked_item_id,
    linkedItem?.item_name,
    addon?.linked_variant_id,
    linkedVariant?.variant_name,
    addon?.price_type,
    addon?.price,
    addon?.status,
  ];

  return searchableFields.some((value) => normalize(value).includes(query));
});

const ssoUsername = localStorage.getItem("anlook_sso_username") || "";


  const getFixedRestaurant = (rows) => {
    if (!ssoUsername.trim()) return null;

    const normalizeName = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/marketting/g, "marketing");

    return (Array.isArray(rows) ? rows : []).find(
      (r) => normalizeName(r?.restaurant_name) === normalizeName(ssoUsername),
    );
  };

  const linkedItemVariants = form.linked_item_id
    ? variants.filter(
        (variant) =>
          String(variant?.item_id ?? "") === String(form.linked_item_id),
      )
    : [];

  const load = async () => {
    const addonRows = await getAddons("");
    setRows(Array.isArray(addonRows) ? addonRows : []);
  };

  const ensureAddonOptions = async () => {
    const [restaurantRowsRaw, itemRowsRaw, variantRowsRaw] = await Promise.all([
      restaurants.length ? Promise.resolve(restaurants) : getRestaurants(""),
      items.length ? Promise.resolve(items) : getItems("", ""),
      variants.length ? Promise.resolve(variants) : getItemVariants(""),
    ]);
    const restaurantRows = Array.isArray(restaurantRowsRaw)
      ? restaurantRowsRaw
      : [];
    const itemRows = Array.isArray(itemRowsRaw) ? itemRowsRaw : [];
    const variantRows = Array.isArray(variantRowsRaw) ? variantRowsRaw : [];
    setRestaurants(restaurantRows);
    setItems(itemRows);
    setVariants(variantRows);
    return { restaurantRows, itemRows, variantRows };
  };

 useEffect(() => {
   load();
   ensureAddonOptions();

   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 const edit = async (row) => {
   const { restaurantRows } = await ensureAddonOptions();
   const restaurant = getFixedRestaurant(restaurantRows);

   if (!restaurant) {
     alert("SSO restaurant not found.");
     return;
   }

   const restaurantId = String(restaurant.restaurant_id ?? restaurant.id ?? "");

   if (String(row?.restaurant_id ?? "") !== restaurantId) {
     alert("This addon belongs to another restaurant.");
     return;
   }

   setForm({
     addon_id: String(row?.addon_id ?? ""),
     restaurant_id: restaurantId,
     addon_name: String(row?.addon_name ?? ""),
     linked_item_id: String(row?.linked_item_id ?? ""),
     linked_variant_id: String(row?.linked_variant_id ?? ""),
     price_type: String(row?.price_type ?? ""),
     price: String(row?.price ?? ""),
     status: String(row?.status ?? "ACTIVE"),
   });

   setModal(true);
 };

  const submit = async (event) => {
    event.preventDefault();
    const saved = form.addon_id
      ? await updateAddon(form)
      : await insertAddon(form);
    if (saved) {
      setModal(false);
      setForm(EMPTY_ADDON);
      load();
    }
  };

  const getName = (list, idKey, nameKey, id, label) =>
    list.find((row) => String(row?.[idKey] ?? row?.id) === String(id))?.[
      nameKey
    ] || `${label} #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Addons"
        subtitle="Manage restaurant addons, linked items, variants and prices."
        loading={addonsLoading}
        searchValue={addonSearch}
        onSearchChange={setAddonSearch}
        searchPlaceholder="Search addon ID, name, item, price..."
        onRefresh={load}
        onAdd={async () => {
          const { restaurantRows } = await ensureAddonOptions();
          const restaurant = getFixedRestaurant(restaurantRows);

          if (!restaurant) {
            alert("SSO restaurant not found.");
            return;
          }

          setForm({
            ...EMPTY_ADDON,
            restaurant_id: String(
              restaurant.restaurant_id ?? restaurant.id ?? "",
            ),
          });

          setModal(true);
        }}
        add="Add Addon"
      />

      <Rows
        loading={addonsLoading}
        empty={
          addonSearch.trim()
            ? "No addons match your search."
            : "No addons found."
        }
        rows={filteredAddons}
        render={(row, index) => {
          const id = String(row?.addon_id ?? "");
          return (
            <div
              key={id || index}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">
                  {row?.addon_name || "Unnamed Addon"}
                </b>
                <p className="text-[11px] text-slate-500">
                  ID #{id || "—"} ·{" "}
                  {getName(
                    restaurants,
                    "restaurant_id",
                    "restaurant_name",
                    row?.restaurant_id,
                    "Restaurant",
                  )}{" "}
                  · Item{" "}
                  {row?.linked_item_id
                    ? getName(
                        items,
                        "item_id",
                        "item_name",
                        row.linked_item_id,
                        "Item",
                      )
                    : "—"}{" "}
                  · Variant{" "}
                  {row?.linked_variant_id
                    ? getName(
                        variants,
                        "variant_id",
                        "variant_name",
                        row.linked_variant_id,
                        "Variant",
                      )
                    : "—"}{" "}
                  ·{row?.price_type || "—"} ₹{row?.price || "0"} ·{" "}
                  {row?.status || "—"}
                </p>
              </div>
              <Icon onClick={() => edit(row)}>
                <Edit3 size={13} />
              </Icon>
              <Icon
                danger
                disabled={addonDeletingId === id}
                onClick={async () => {
                  if (await deleteAddon(id)) load();
                }}
              >
                {addonDeletingId === id ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />

      {modal && (
        <Modal
          title={form.addon_id ? "Edit Addon" : "Add Addon"}
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <label>
                <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Restaurant
                </span>

                <input
                  type="text"
                  value={ssoUsername}
                  readOnly
                  className="h-10 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none"
                />
              </label>

              <Input
                label="Addon Name"
                name="addon_name"
                form={form}
                setForm={setForm}
              />

              <SearchableLinkedItemDropdown
                items={items}
                value={form.linked_item_id}
                disabled={addonSaving}
                onChange={(id) =>
                  setForm((prev) => ({
                    ...prev,
                    linked_item_id: id,
                    linked_variant_id: "",
                  }))
                }
              />

              <Select
                label="Linked Variant"
                value={form.linked_variant_id}
                set={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    linked_variant_id: value,
                  }))
                }
              >
                <option value="">Select Variant</option>

                {linkedItemVariants.map((row) => (
                  <option key={row?.variant_id} value={row?.variant_id}>
                    {row?.variant_name || `Variant #${row?.variant_id}`}
                    {" — ID: "}
                    {row?.variant_id}
                  </option>
                ))}
              </Select>

              <Select
                label="Price Type"
                value={form.price_type || ""}
                set={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    price_type: value,
                  }))
                }
              >
                <option value="">Select Price Type</option>
                <option value="FIXED">Fixed</option>
                <option value="PER_PERSON">Per Person</option>
                <option value="PER_UNIT">Per Unit</option>
              </Select>

              <Input
                label="Price"
                name="price"
                type="number"
                form={form}
                setForm={setForm}
              />

              <Select
                label="Status"
                value={form.status}
                set={(value) => setForm((prev) => ({ ...prev, status: value }))}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </Select>
            </div>
            <Footer
              saving={addonSaving}
              edit={!!form.addon_id}
              close={() => setModal(false)}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

export function BulkPackageAddons() {
  const {
    packageAddonsLoading,
    packageAddonSaving,
    packageAddonDeletingKey,
    getAddons,
    getPackageAddons,
    insertPackageAddon,
    updatePackageAddon,
    deletePackageAddon,
  } = useBulkAddons();
  const { getPackages } = useBulkPackages();


const [rows, setRows] = useState([]);
const [packages, setPackages] = useState([]);
const [addons, setAddons] = useState([]);
const [form, setForm] = useState(EMPTY_PACKAGE_ADDON);
const [editing, setEditing] = useState(false);
const [modal, setModal] = useState(false);

const [packageAddonSearch, setPackageAddonSearch] = useState("");


const filteredPackageAddons = (() => {
  const normalize = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const query = normalize(packageAddonSearch);

  // Keep one entry for each Package + Addon pair
  const uniqueMappings = new Map();

  rows.forEach((row) => {
    const packageId = String(row?.package_id ?? "").trim();
    const addonId = String(row?.addon_id ?? "").trim();
    const key = `${packageId}:${addonId}`;

    if (!uniqueMappings.has(key)) {
      uniqueMappings.set(key, row);
    }
  });

  const uniqueRows = [...uniqueMappings.values()];

  if (!query) return uniqueRows;

  // Search by numeric IDs
  if (/^\d+$/.test(query)) {
    return uniqueRows.filter((row) => {
      const packageId = String(row?.package_id ?? "").trim();
      const addonId = String(row?.addon_id ?? "").trim();

      return packageId.includes(query) || addonId.includes(query);
    });
  }

  const words = query.split(/\s+/).filter(Boolean);

  const matchesName = (name) => {
    const normalizedName = normalize(name);
    return words.every((word) => normalizedName.includes(word));
  };

  // Step 1: Find matching package IDs using package_name
  const matchingPackageIds = new Set(
    packages
      .filter((pkg) => matchesName(pkg?.package_name))
      .map((pkg) => String(pkg?.package_id ?? pkg?.id ?? "").trim()),
  );

  // Package name gets priority over addon name
  if (matchingPackageIds.size > 0) {
    return uniqueRows.filter((row) =>
      matchingPackageIds.has(String(row?.package_id ?? "").trim()),
    );
  }

  // Fallback if mapping response itself contains a package name
  const directMatches = uniqueRows.filter((row) =>
    matchesName(row?.package_name),
  );

  if (directMatches.length > 0) {
    return directMatches;
  }

  // Step 2: If no package matches, search addon names
  const matchingAddonIds = new Set(
    addons
      .filter((addon) => matchesName(addon?.addon_name))
      .map((addon) => String(addon?.addon_id ?? addon?.id ?? "").trim()),
  );

  return uniqueRows.filter((row) =>
    matchingAddonIds.has(String(row?.addon_id ?? "").trim()),
  );
})();


const load = async () => {
  const [mappingRows, packageRows, addonRows] = await Promise.all([
    getPackageAddons("", ""),
    getPackages(""),
    getAddons(""),
  ]);

  setRows(Array.isArray(mappingRows) ? mappingRows : []);
  setPackages(Array.isArray(packageRows) ? packageRows : []);
  setAddons(Array.isArray(addonRows) ? addonRows : []);
};


  const ensurePackageAddonOptions = async () => {
    const [packageRowsRaw, addonRowsRaw] = await Promise.all([
      packages.length ? Promise.resolve(packages) : getPackages(""),
      addons.length ? Promise.resolve(addons) : getAddons(""),
    ]);
    const packageRows = Array.isArray(packageRowsRaw) ? packageRowsRaw : [];
    const addonRows = Array.isArray(addonRowsRaw) ? addonRowsRaw : [];
    setPackages(packageRows);
    setAddons(addonRows);
    return { packageRows, addonRows };
  };

useEffect(() => {
  load();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const edit = async (row) => {
  await ensurePackageAddonOptions();
  const packageId = String(row?.package_id ?? "");
  const addonId = String(row?.addon_id ?? "");
  setForm({
    package_id: packageId,
    addon_id: addonId,
    new_package_id: packageId,
    new_addon_id: addonId,
  });
  setEditing(true);
  setModal(true);
};

  const submit = async (event) => {
    event.preventDefault();
    const saved = editing
      ? await updatePackageAddon(form)
      : await insertPackageAddon(form);

    if (saved) {
      setModal(false);
      setEditing(false);
      setForm(EMPTY_PACKAGE_ADDON);
      load();
    }
  };

  const packageName = (id) =>
    packages.find((row) => String(row?.package_id) === String(id))
      ?.package_name || `Package #${id || "—"}`;

  const addonName = (id) =>
    addons.find((row) => String(row?.addon_id) === String(id))?.addon_name ||
    `Addon #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Package Addons"
        subtitle="Map addons with packages."
        loading={packageAddonsLoading}
        searchValue={packageAddonSearch}
        onSearchChange={setPackageAddonSearch}
        searchPlaceholder="Search package ID, name, addon..."
        onRefresh={load}
        onAdd={async () => {
          const { packageRows, addonRows } = await ensurePackageAddonOptions();
          setForm({
            ...EMPTY_PACKAGE_ADDON,
            package_id: String(packageRows?.[0]?.package_id ?? ""),
            addon_id: String(addonRows?.[0]?.addon_id ?? ""),
          });
          setEditing(false);
          setModal(true);
        }}
        add="Add Mapping"
      />

      <Rows
        loading={packageAddonsLoading}
        empty={
          packageAddonSearch.trim()
            ? "No package addons match your search."
            : "No package addons found."
        }
        rows={filteredPackageAddons}
        render={(row, index) => {
          const packageId = String(row?.package_id ?? "");
          const addonId = String(row?.addon_id ?? "");
          const key = `${packageId}:${addonId}`;

          return (
            <div
              key={key || index}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">
                  {packageName(packageId)}
                </b>
                <p className="text-[11px] text-slate-500">
                  {addonName(addonId)} · Package #{packageId || "—"} · Addon #
                  {addonId || "—"}
                </p>
              </div>
              <Icon onClick={() => edit(row)}>
                <Edit3 size={13} />
              </Icon>
              <Icon
                danger
                disabled={packageAddonDeletingKey === key}
                onClick={async () => {
                  if (await deletePackageAddon(packageId, addonId)) load();
                }}
              >
                {packageAddonDeletingKey === key ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />

      {modal && (
        <Modal
          title={editing ? "Edit Package Addon" : "Add Package Addon"}
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <SearchableLinkedItemDropdown
                label={editing ? "Current Package" : "Package"}
                items={packages}
                idKey="package_id"
                nameKey="package_name"
                value={form.package_id}
                disabled={editing || packageAddonSaving}
                onChange={(id) =>
                  setForm((prev) => ({
                    ...prev,
                    package_id: id,
                  }))
                }
              />

              <SearchableLinkedItemDropdown
                label={editing ? "Current Addon" : "Addon"}
                items={addons}
                idKey="addon_id"
                nameKey="addon_name"
                value={form.addon_id}
                disabled={editing || packageAddonSaving}
                onChange={(id) =>
                  setForm((prev) => ({
                    ...prev,
                    addon_id: id,
                  }))
                }
              />

              {editing && (
                <>
                  <SearchableLinkedItemDropdown
                    label="New Package"
                    items={packages}
                    idKey="package_id"
                    nameKey="package_name"
                    value={form.new_package_id}
                    disabled={packageAddonSaving}
                    onChange={(id) =>
                      setForm((prev) => ({
                        ...prev,
                        new_package_id: id,
                      }))
                    }
                  />

                  <SearchableLinkedItemDropdown
                    label="New Addon"
                    items={addons}
                    idKey="addon_id"
                    nameKey="addon_name"
                    value={form.new_addon_id}
                    disabled={packageAddonSaving}
                    onChange={(id) =>
                      setForm((prev) => ({
                        ...prev,
                        new_addon_id: id,
                      }))
                    }
                  />
                </>
              )}
            </div>
            <Footer
              saving={packageAddonSaving}
              edit={editing}
              close={() => setModal(false)}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

function Header({
  title,
  subtitle,
  loading,
  onRefresh,
  onAdd,
  add,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search...",
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="text-[11px] text-slate-500">{subtitle}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {typeof onSearchChange === "function" && (
          <div className="relative w-full sm:w-64 lg:w-80">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />

            {searchValue && (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />{" "}
          Refresh
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
        >
          <Plus size={13} /> {add}
        </button>
      </div>
    </div>
  );
}

function Rows({ loading, empty, rows, render }) {
  if (loading)
    return <div className="h-24 animate-pulse rounded-xl border bg-white" />;
  if (!rows?.length)
    return (
      <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
        {empty}
      </div>
    );
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {rows.map(render)}
    </div>
  );
}

function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{title}</h3>
          <button type="button" onClick={close}>
            <X size={17} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}


function SearchableLinkedItemDropdown({
  items = [],
  value,
  onChange,
  disabled = false,
  label = "Linked Item",
  idKey = "item_id",
  nameKey = "item_name",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

const getId = (row) => String(row?.[idKey] ?? row?.id ?? "");

const getName = (row) => row?.[nameKey] || `${label} #${getId(row)}`;

  const selected = items.find((row) => getId(row) === String(value));

 
const normalizeSearch = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const filtered = items.filter((row) => {
  const query = normalizeSearch(search);

  if (!query) return true;

  const searchText = normalizeSearch(`${getName(row)} ${getId(row)}`);

  const queryWords = query.split(/\s+/).filter(Boolean);

  return (
    queryWords.every((word) => searchText.includes(word)) ||
    searchText.replace(/\s+/g, "").includes(query.replace(/\s+/g, ""))
  );
});


  useEffect(() => {
    if (!open) return;

    searchRef.current?.focus();

    const handleOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  const chooseItem = (row) => {
    onChange(getId(row));
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={dropdownRef} className={`relative min-w-0 ${open ? "z-50" : ""}`}>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((prev) => !prev);
          setSearch("");
        }}
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        className={`flex h-10 w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 text-left text-sm shadow-sm transition outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
          open
            ? "border-emerald-400 ring-2 ring-emerald-100"
            : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <span
          className={`truncate ${
            selected ? "text-slate-800" : "text-slate-400"
          }`}
        >
          {selected
            ? getName(selected)
            : value
              ? `${label} #${value}`
              : `Select ${label}`}
        </span>

        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 top-full z-[100] mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
          {/* SEARCH BAR */}
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();

                    if (filtered.length) {
                      chooseItem(filtered[0]);
                    }
                  }

                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  }
                }}
                placeholder={`Search ${label.toLowerCase()} by name or ID...`}
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none"
              />
            </div>
          </div>

          {/* 6 OPTIONS - SCROLLBAR HIDDEN */}
          <div
            role="listbox"
            aria-label="Linked Items"
            className="max-h-[216px] overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {!search.trim() && (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                  setSearch("");
                }}
                className={`flex h-9 w-full items-center px-3 text-left text-sm transition ${
                  !value
                    ? "bg-emerald-50 font-medium text-emerald-700"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                Select Item
              </button>
            )}

            {filtered.length > 0 ? (
              filtered.map((row) => {
                const id = getId(row);
                const active = String(value) === id;

                return (
                  <button
                    key={id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => chooseItem(row)}
                    className={`flex h-9 w-full items-center px-3 text-left text-sm transition ${
                      active
                        ? "bg-emerald-50 font-medium text-emerald-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="truncate">{getName(row)}</span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-4 text-center text-sm text-slate-400">
                No items found
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function Select({ label, value, set, children, disabled = false }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => set(event.target.value)}
        disabled={disabled}
        className={field}
      >
        {children}
      </select>
    </label>
  );
}

function Input({ label, name, form, setForm, type = "text" }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <input
        type={type}
        value={form[name]}
        onChange={(event) =>
          setForm((prev) => ({ ...prev, [name]: event.target.value }))
        }
        className={field}
      />
    </label>
  );
}

function Icon({ children, danger, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border ${danger ? "border-rose-100 bg-rose-50 text-rose-600" : "border-slate-200 text-slate-500"}`}
    >
      {children}
    </button>
  );
}

function Footer({ saving, edit, close }) {
  return (
    <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
      <button
        type="button"
        onClick={close}
        disabled={saving}
        className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={saving}
        className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
      >
        {saving ? (
          <Loader2 size={13} className="animate-spin" />
        ) : edit ? (
          <Edit3 size={13} />
        ) : (
          <Plus size={13} />
        )}
        {saving ? "Saving..." : edit ? "Update" : "Add"}
      </button>
    </div>
  );
}
